import React, { useState, useCallback, useEffect } from "react";
import Cropper from "react-easy-crop";
import { toast } from "react-toastify";
import Button from "./../../components/Button/Button";
import { FaTimes, FaSave } from "react-icons/fa";

const backdrop =
  "fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[80] flex items-center justify-center p-4";

// ——— Helpers
function toMasterUrlInPlace(url = "") {
  if (!url) return "";
  const [base, query = ""] = url.split("?");
  const masterBase = base.replace(/-400(\.\w+)$/, "-master$1");
  return query ? `${masterBase}?${query}` : masterBase;
}
function withCacheBust(u) {
  if (!u) return u;
  const sep = u.includes("?") ? "&" : "?";
  return `${u}${sep}cb=${Date.now()}`;
}

/**
 * Modale générique de recadrage (1:1)
 *
 * Props:
 * - isOpen, onClose
 * - entityId: ID de l’entité (adherentId, teamId, etc.)
 * - imageUrl: URL (thumb -400 ou master) affichée côté UI
 * - cropAction: (id, payload) => Promise<{ photo_url? | photoURL? }>
 * - onCropped: (newPublicUrl) => void
 * - format: "webp" | "jpg" | "png" (défaut "webp")
 */
const CropPhotoModal = ({
  isOpen,
  onClose,
  entityId,
  imageUrl,
  cropAction,
  onCropped,
  format = "webp",
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1.2);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  const [src, setSrc] = useState(""); // URL MASTER + cache-bust pour le Cropper
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const prepareImage = useCallback(() => {
    if (!imageUrl) return;
    setLoading(true);
    setLoadError("");
    const master = withCacheBust(toMasterUrlInPlace(imageUrl));
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      setSrc(master);
      setLoading(false);
      setLoadError("");
    };
    img.onerror = () => {
      setLoading(false);
      setLoadError("Impossible de charger la photo source (master).");
    };
    img.src = master;
  }, [imageUrl]);

  useEffect(() => {
    if (!isOpen) return;
    prepareImage();
  }, [isOpen, prepareImage]);

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!entityId || !croppedAreaPixels) return;
    if (loadError) {
      toast.error("L'image n'est pas chargée, recadrage impossible.");
      return;
    }
    if (typeof cropAction !== "function") {
      toast.error("Action de recadrage indisponible.");
      return;
    }
    try {
      setSaving(true);
      const res = await cropAction(entityId, {
        x: Math.round(croppedAreaPixels.x),
        y: Math.round(croppedAreaPixels.y),
        width: Math.round(croppedAreaPixels.width),
        height: Math.round(croppedAreaPixels.height),
        format,
        // src optionnel: les contrôleurs savent dériver depuis photo_url/photoURL
      });
      const newUrl = res?.photo_url || res?.photoURL;
      if (!newUrl) {
        toast.warn("Recadrage effectué, mais URL non renvoyée par le serveur.");
      } else {
        toast.success("Photo recadrée.");
        onCropped?.(newUrl);
      }
      onClose?.();
    } catch (e) {
      console.error(e);
      toast.error("Échec du recadrage.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={backdrop} onClick={onClose}>
      <div
        className="relative w-full max-w-lg rounded-2xl shadow-2xl bg-white dark:bg-slate-900 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800">
          <h4 className="text-sm font-semibold">Recadrer / Zoomer</h4>
        </div>

        <div className="relative h-[55vh] bg-black">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center text-white text-sm">
              Chargement de l’image…
            </div>
          )}

          {loadError && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center p-4 gap-3">
              <div className="text-sm">{loadError}</div>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  prepareImage();
                }}
              >
                Réessayer
              </button>
            </div>
          )}

          {!loading && !loadError && src && (
            <Cropper
              key={src}
              image={src}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              showGrid={false}
              restrictPosition={true}
              mediaProps={{ crossOrigin: "anonymous", draggable: false }}
              objectFit="contain"
            />
          )}
        </div>

        <div className="p-4 space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-sm w-28">Zoom</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="range range-sm flex-1"
              disabled={!!loadError || loading}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              text="Annuler"
              icon={FaTimes}
              onClick={(e) => {
                e.stopPropagation();
                onClose?.();
              }}
              className="gap-2"
              disabled={saving}
            />
            <Button
              text={saving ? "Enregistrement..." : "Enregistrer"}
              icon={FaSave}
              onClick={handleSave}
              disabled={saving || !!loadError || loading}
              className="gap-2"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CropPhotoModal;

// util exportée si besoin ailleurs
export function toMasterUrl(thumbUrl = "") {
  if (!thumbUrl) return "";
  return thumbUrl.replace(/-400(\.\w+)$/, "-master$1");
}
